import { useEffect, useState } from 'react';

/**
 * Per-session de-dup set keyed by the caller's impressionKey. Mixpanel
 * itself dedup's on `$insert_id`, but we also don't want to fire the
 * callback at all on repeat views — saves work for downstream side
 * effects (api calls, expensive computations).
 */
const firedKeys = new Set<string>();

/**
 * Test-only escape hatch. Internal to the analytics surface.
 */
export function __resetImpressionStateForTests(): void {
  firedKeys.clear();
}

export interface ImpressionOptions {
  /** Visibility threshold (0–1). Default 0.5. */
  threshold?: number;
  /** Disable the observer entirely (e.g. before data has loaded). */
  enabled?: boolean;
  /** Override IntersectionObserver for tests. */
  observerFactory?: (cb: IntersectionObserverCallback, init?: IntersectionObserverInit) => IntersectionObserver;
}

/**
 * Returns a `ref` to attach to the impression target. Fires `onSeen`
 * exactly once per `impressionKey` per browser session (in-memory).
 *
 *   const ref = useTrackImpression(`${entityId}:${variationId}`, () => {
 *     track('page_viewed', { path });
 *   });
 *   return <div ref={ref}>...</div>
 *
 * Designed for analytics — not for triggering data fetches. The session
 * dedup is intentional; refresh-the-page or open-in-new-tab still fires.
 */
export function useTrackImpression(
  impressionKey: string,
  onSeen: () => void,
  options: ImpressionOptions = {},
): (node: Element | null) => void {
  const { threshold = 0.5, enabled = true, observerFactory } = options;
  const [node, setNode] = useState<Element | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (!node) return;
    if (firedKeys.has(impressionKey)) return;
    if (typeof IntersectionObserver === 'undefined' && !observerFactory) return;

    const factory =
      observerFactory ??
      ((cb: IntersectionObserverCallback, init?: IntersectionObserverInit) => new IntersectionObserver(cb, init));

    const observer = factory(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (firedKeys.has(impressionKey)) return;
          firedKeys.add(impressionKey);
          try {
            onSeen();
          } catch {
            // Tracking must never crash the host component
          }
          observer.disconnect();
          return;
        }
      },
      { threshold },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, enabled, threshold, impressionKey, observerFactory, onSeen]);

  // React calls the ref setter with the DOM node (or null on unmount).
  // useState's setter has a compatible signature, so we use it directly
  // as the ref callback — no extra wrapper, no manual cleanup ordering.
  return setNode;
}
